/**
 * @file ElementRow.tsx
 * @description Строка элемента в сетке секции (phase19.16 Р2; uiux-design-r2
 * §4): ⋮⋮ (перетаскивание внутри секции и между колонками), значок и
 * сводка «Кнопка: Выбрать чай», отметка замечания, «⋯» — Дублировать ·
 * Выше · Ниже · Удалить. Нажатие открывает панель элемента.
 *
 * Р2а (uiux-design-r2a §3, §6): у обязательного элемента шаблона — замок
 * (з) и «Удалить» недоступно с причиной; элемент данных товара не
 * дублируется (в шаблоне он один); «Перенести в другой блок ›» — у любого.
 */

import { isProductRequiredKind, type SectionElement } from "@chaiberi/shared";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { EyeOff, GripVertical, Lock } from "lucide-react";

import { RowActionsMenu } from "../../../ui/RowActionsMenu";
import { useSectionForm } from "../section-form-context";
import { elementMenuActions, type ElementRowActions } from "./element-actions";
import { ELEMENT_KIND_META, elementSummary } from "../../../../lib/site-builder/section-meta";
import { cn } from "@/lib/utils";

interface Props {
  element: SectionElement;
  selected: boolean;
  first: boolean;
  last: boolean;
  invalid: boolean;
  onSelect: () => void;
  actions: ElementRowActions;
}

export function ElementRow({ element, selected, first, last, invalid, onSelect, actions }: Props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: element.id });
  const { moveElement } = useSectionForm();
  const meta = ELEMENT_KIND_META[element.kind];
  const Icon = meta.icon;
  const hidden = element.hide && (element.hide.phone || element.hide.tablet || element.hide.desktop);
  const required = isProductRequiredKind(element.kind);
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "flex min-h-12 items-center gap-1 rounded-lg px-1",
        selected ? "bg-primary/10" : "bg-surface-lowest hover:bg-surface-low",
        isDragging && "relative z-10 opacity-70 shadow-lg",
        invalid && "[box-shadow:inset_0_0_0_1px_var(--destructive)]",
      )}
    >
      <button type="button" {...attributes} {...listeners} aria-label="Перетащить элемент" className="inline-flex size-10 shrink-0 cursor-grab touch-none items-center justify-center text-on-surface-variant max-lg:size-12">
        <GripVertical aria-hidden className="size-4" />
      </button>
      {/* data-invalid — сюда фокус после неудачного «Сохранить блок» (SB122). */}
      <button type="button" onClick={onSelect} aria-current={selected ? "true" : undefined} data-invalid={invalid || undefined} className="flex min-h-12 min-w-0 flex-1 items-center gap-2 text-left">
        <Icon aria-hidden className="size-4 shrink-0 text-on-surface-variant" />
        <span className="type-body min-w-0 truncate text-on-surface" title={elementSummary(element)}>
          {elementSummary(element)}
        </span>
        {required ? <Lock aria-label="Обязательный элемент" className="size-4 shrink-0 text-on-surface-variant" /> : null}
        {hidden ? <EyeOff aria-label="Скрыт на части устройств" className="size-4 shrink-0 text-on-surface-variant" /> : null}
        {invalid ? <span className="type-caption shrink-0 text-destructive">проверьте</span> : null}
      </button>
      <RowActionsMenu
        title={`Элемент «${meta.label}»`}
        size="compact"
        actions={elementMenuActions(element, { first, last }, actions, moveElement ? () => moveElement(element.id) : null)}
      />
    </li>
  );
}
